<script setup>
import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useCurrency } from '@/composables/useCurrency.js';

const props = defineProps({
  // Always receives and emits INTEGER cents
  modelValue: {
    type: Number,
    default: 0,
    validator: (v) => typeof v === 'number' && v >= 0,
  },
  placeholder: {
    type: String,
    default: null,
  },
  min: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: null,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue', 'blur', 'focus']);

const { t } = useI18n();
const { createCurrencyInput } = useCurrency();

// Create currency input with auto-emit disabled (we handle it manually)
const { inputRef, numberValue, setValue } = createCurrencyInput(false);

// Sync with parent value (in cents)
watch(
  () => props.modelValue,
  (newValue) => {
    if (newValue !== numberValue.value) {
      setValue(newValue);
    }
  },
  { immediate: true },
);

// Apply min/max constraints and emit changes
watch(numberValue, (newValue) => {
  if (newValue == null) {
    emit('update:modelValue', 0);
    return;
  }

  let constrainedValue = Math.round(newValue);

  if (props.min !== null && constrainedValue < props.min) {
    constrainedValue = props.min;
    setValue(constrainedValue);
  }

  if (props.max !== null && constrainedValue > props.max) {
    constrainedValue = props.max;
    setValue(constrainedValue);
  }

  if (constrainedValue !== props.modelValue) {
    emit('update:modelValue', constrainedValue);
  }
});

const handleBlur = (event) => {
  emit('blur', event);
};

const handleFocus = (event) => {
  emit('focus', event);
};

const placeholderText = computed(() => {
  return props.placeholder || t('item.reservation.depositAmount');
});
</script>

<template>
  <div class="money-input-wrapper">
    <input
      ref="inputRef"
      type="text"
      inputmode="decimal"
      :placeholder="placeholderText"
      :disabled="disabled"
      :readonly="readonly"
      class="money-input"
      @blur="handleBlur"
      @focus="handleFocus"
    />
    <!--    <span class="currency-symbol">ج.م</span>-->
  </div>
</template>

<style scoped>
.money-input-wrapper {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    background: var(--f7-page-bg-color);
    border: 1px solid var(--f7-list-item-border-color);
    border-radius: 8px;
    transition: all 0.2s;
}

.money-input-wrapper:focus-within {
    border-color: var(--f7-theme-color);
    box-shadow: 0 0 0 3px rgba(var(--f7-theme-color-rgb), 0.1);
}

.money-input {
    flex: 1;
    border: none;
    outline: none;
    background: transparent;
    font-size: 14px;
    font-weight: 500;
    text-align: right;
    color: var(--f7-text-color);
    font-family: inherit;
    direction: ltr;
}

.money-input::placeholder {
    color: var(--f7-input-placeholder-color);
    opacity: 0.6;
}

.money-input:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.currency-symbol {
    font-size: 13px;
    color: var(--f7-text-color);
    opacity: 0.7;
    font-weight: 500;
    flex-shrink: 0;
}
</style>
